import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaBook, FaSearch, FaFileExcel,
  FaTimes, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './JournalSalary.css';

/** Unpaid salary: Cr only when payment mode was set; else empty (not paid yet) */
const getCrLedgerFromSalary = (sal) => {
  const mode = (sal.paymentMode || '').trim();
  if (!mode) return '';
  if (mode.toLowerCase() === 'cash') return 'Cash';
  const bankName = (sal.bankName || sal.bank?.name || '').trim();
  return bankName || mode;
};

const mapApiSalaryToRow = (sal) => {
  const empName = (sal.employee?.name || '').trim();
  const deptName = (sal.employee?.department?.name || '').trim();
  const drLedger = deptName ? `${empName} (${deptName})` : empName;

  const amount = Number(sal.netSalary) || 0;

  return {
    _id: sal._id,
    date: sal.createdAt || sal.updatedAt,
    vchNo: (sal.slipNo || '').trim(),
    drLedger,
    crLedger: getCrLedgerFromSalary(sal),
    amountDr: amount,
    amountCr: amount,
    narration: (sal.narration || '').trim(),
    college: sal.college,
    paymentMode: sal.paymentMode,
    bankName: sal.bankName,
    bank: sal.bank,
  };
};

const getDrLedger = (row) => row.drLedger ?? '';
const getCrLedger = (row) => row.crLedger ?? getCrLedgerFromSalary(row);
const getVchNo = (row) => row.vchNo ?? '';
const getNarration = (row) => row.narration ?? '';

const JournalSalary = () => {
  const { user } = useAuth();
  const { colleges: collegesList, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (isScoped && collegesList.length > 0 && !filterCollege) {
      setFilterCollege(collegesList[0]._id);
    }
  }, [isScoped, collegesList, filterCollege]);

  const fetchJournals = useCallback(async () => {
    if (!filterCollege) {
      setJournals([]);
      setFetchError('');
      return;
    }

    setLoading(true);
    setFetchError('');
    setJournals([]);

    try {
      const { data } = await api.get('/salary', { params: { college: filterCollege, _t: Date.now() } });
      const unpaid = (data?.data || [])
        .filter((s) => !s.isPaid && String(s.college?._id || s.college) === String(filterCollege));

      let rows = unpaid.map(mapApiSalaryToRow);

      if (fromDate || toDate) {
        rows = rows.filter((row) => {
          if (!row.date) return false;
          const d = new Date(row.date);
          if (fromDate) {
            const f = new Date(fromDate);
            f.setHours(0, 0, 0, 0);
            if (d < f) return false;
          }
          if (toDate) {
            const t = new Date(toDate);
            t.setHours(23, 59, 59, 999);
            if (d > t) return false;
          }
          return true;
        });
      }

      rows.sort((a, b) => new Date(a.date) - new Date(b.date));
      setJournals(rows);
    } catch (err) {
      setFetchError(err.response?.data?.message || 'Failed to load journal salary data');
      setJournals([]);
    } finally {
      setLoading(false);
    }
  }, [filterCollege, fromDate, toDate]);

  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  const handleClearFilters = () => {
    setSearchQuery('');
    if (!isScoped) setFilterCollege('');
    setFromDate('');
    setToDate('');
    if (!isScoped) setJournals([]);
  };

  const filteredJournals = useMemo(() => {
    const search = searchQuery.toLowerCase().trim();
    return journals.filter((row) => {
      if (!search) return true;
      const drLedger = getDrLedger(row).toLowerCase();
      const crLedger = getCrLedger(row).toLowerCase();
      const narration = getNarration(row).toLowerCase();
      const vchNo = getVchNo(row).toLowerCase();
      return (
        drLedger.includes(search) ||
        crLedger.includes(search) ||
        narration.includes(search) ||
        vchNo.includes(search)
      );
    });
  }, [journals, searchQuery]);

  const selectedCollege = useMemo(
    () => collegesList.find((c) => String(c._id) === String(filterCollege)),
    [collegesList, filterCollege]
  );

  const totalAmountDr = useMemo(
    () => filteredJournals.reduce((sum, row) => sum + (Number(row.amountDr) || 0), 0),
    [filteredJournals]
  );

  const totalAmountCr = useMemo(
    () => filteredJournals.reduce((sum, row) => sum + (Number(row.amountCr) || 0), 0),
    [filteredJournals]
  );

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleExportExcel = () => {
    const headers = [
      'Date', 'VCh No', 'Dr Ledger', 'Cr Ledger', 'Amount Dr', 'Amount Cr', 'Narations',
    ];

    const rows = filteredJournals.map((row) => [
      row.date ? new Date(row.date).toLocaleDateString('en-GB') : '',
      getVchNo(row),
      getDrLedger(row),
      getCrLedger(row),
      Number(row.amountDr) || 0,
      Number(row.amountCr) || 0,
      getNarration(row),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 35 },
      { wch: 25 },
      { wch: 14 },
      { wch: 14 },
      { wch: 45 },
    ];

    const filenameParts = ['Journal_Salary_Register'];
    if (selectedCollege) {
      const cleanName = selectedCollege.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/__+/g, '_');
      filenameParts.push(cleanName);
    }
    if (fromDate && toDate) filenameParts.push(`${fromDate}_to_${toDate}`);
    else if (fromDate) filenameParts.push(`from_${fromDate}`);
    else if (toDate) filenameParts.push(`to_${toDate}`);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Journal Salary');
    XLSX.writeFile(wb, `${filenameParts.join('_')}.xlsx`);
  };

  const showSelectCollegeMsg = !filterCollege && !collegesLoading;

  return (
    <div className="tele-export-journal-container">
      <div className="tele-export-journal-header-row">
        <h2 className="tele-export-journal-title">
          <FaBook /> Journal Salary Register
        </h2>
        <div className="tele-export-journal-export-btns">
          <button
            type="button"
            className="tele-export-journal-btn-excel"
            onClick={handleExportExcel}
            disabled={filteredJournals.length === 0}
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {filterCollege && filteredJournals.length > 0 && (
        <div className="tele-export-journal-summary-strip">
          <span>
            Showing <strong>{filteredJournals.length}</strong> created (unpaid) salaries
            {selectedCollege ? <> for <strong>{selectedCollege.name}</strong></> : null}
          </span>
          <div className="tele-export-journal-summary-amounts">
            <span className="tele-export-journal-summary-amount">
              Total Dr: <strong>₹ {totalAmountDr.toLocaleString()}</strong>
            </span>
            <span className="tele-export-journal-summary-amount" style={{ marginLeft: '15px' }}>
              Total Cr: <strong>₹ {totalAmountCr.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}

      <div className="tele-export-journal-controls">
        <div className="tele-export-journal-search-wrapper">
          <FaSearch className="tele-export-journal-search-icon" />
          <input
            type="text"
            className="tele-export-journal-search-input"
            placeholder="Search employee, department, bank..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!filterCollege}
          />
        </div>

        {!isScoped && (
          <select
            className="tele-export-journal-select"
            value={filterCollege}
            onChange={(e) => setFilterCollege(e.target.value)}
            disabled={collegesLoading}
          >
            <option value="">Select College</option>
            {collegesList.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {isScoped && selectedCollege && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 14px',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            🏫 {selectedCollege.name}
          </span>
        )}

        <input
          type="date"
          className="tele-export-journal-select"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From Date"
          disabled={!filterCollege}
        />

        <input
          type="date"
          className="tele-export-journal-select"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          title="To Date"
          disabled={!filterCollege}
        />

        {(filterCollege && !isScoped) || fromDate || toDate || searchQuery ? (
          <button type="button" className="tele-export-journal-btn-clear" onClick={handleClearFilters}>
            <FaTimes /> Clear
          </button>
        ) : null}
      </div>

      <div className="tele-export-journal-grouped-container">
        {collegesLoading ? (
          <div className="tele-export-journal-state-msg">Loading colleges...</div>
        ) : showSelectCollegeMsg ? (
          <div className="tele-export-journal-state-msg tele-export-journal-empty">
            Please select a college to view created (unpaid) salaries for Tally journal export.
          </div>
        ) : loading ? (
          <div className="tele-export-journal-state-msg">Loading records...</div>
        ) : fetchError ? (
          <div className="tele-export-journal-state-msg tele-export-journal-empty">{fetchError}</div>
        ) : filteredJournals.length === 0 ? (
          <div className="tele-export-journal-state-msg tele-export-journal-empty">
            No created (unpaid) salary records found for the selected college and date range.
          </div>
        ) : (
          <div className="tele-export-journal-table-group">
            <h3 className="tele-export-journal-group-header">
              <span className="tele-export-journal-college-name">
                🏫 {selectedCollege?.name || 'College'}
              </span>
              <div className="tele-export-journal-group-stats">
                <span className="tele-export-journal-group-count">
                  {filteredJournals.length} entries
                </span>
                <span className="tele-export-journal-group-total">
                  Dr: ₹ {totalAmountDr.toLocaleString()} | Cr: ₹ {totalAmountCr.toLocaleString()}
                </span>
              </div>
            </h3>
            <div className="tele-export-journal-table-wrapper">
              <table className="tele-export-journal-table">
                <thead>
                  <tr>
                    <th width="55px">S.No</th>
                    <th className="tele-export-journal-col-date">Date</th>
                    <th className="tele-export-journal-col-vch">VCh No</th>
                    <th className="tele-export-journal-col-dr tele-export-journal-align-left">Dr Ledger</th>
                    <th className="tele-export-journal-col-cr tele-export-journal-align-left">Cr Ledger</th>
                    <th className="tele-export-journal-col-amount-dr">Amount Dr</th>
                    <th className="tele-export-journal-col-amount-cr">Amount Cr</th>
                    <th className="tele-export-journal-col-narration tele-export-journal-align-left">Narrations</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJournals.map((row, index) => (
                    <React.Fragment key={row._id || index}>
                      <tr className="tele-export-journal-main-row">
                        <td>{index + 1}</td>
                        <td className="tele-export-journal-col-date" style={{ fontWeight: 600, color: '#334155' }}>
                          {fmtDate(row.date)}
                        </td>
                        <td className="tele-export-journal-col-vch" style={{ fontWeight: 600, color: '#475569' }}>
                          {getVchNo(row)}
                        </td>
                        <td className="tele-export-journal-col-dr tele-export-journal-align-left" style={{ fontWeight: 700, color: '#1e3a5f' }}>
                          {getDrLedger(row)}
                        </td>
                        <td className="tele-export-journal-col-cr tele-export-journal-align-left" style={{ fontWeight: 700, color: '#475569' }}>
                          {getCrLedger(row)}
                        </td>
                        <td className="tele-export-journal-col-amount-dr tele-export-journal-amt-total">
                          ₹ {Number(row.amountDr || 0).toLocaleString()}
                        </td>
                        <td className="tele-export-journal-col-amount-cr tele-export-journal-amt-total" style={{ color: '#16a34a' }}>
                          ₹ {Number(row.amountCr || 0).toLocaleString()}
                        </td>
                        <td className="tele-export-journal-col-narration tele-export-journal-align-left">
                          <div
                            className="tele-export-journal-name-wrapper"
                            onClick={() => {
                              if (getNarration(row)) {
                                setExpandedRow(expandedRow === row._id ? null : row._id);
                              }
                            }}
                            role={getNarration(row) ? 'button' : undefined}
                            tabIndex={getNarration(row) ? 0 : undefined}
                          >
                            <div className="tele-export-journal-truncate-text">
                              {getNarration(row)}
                            </div>
                            {getNarration(row) ? (
                              <button type="button" className="tele-export-journal-expand-btn" aria-label="Expand narration">
                                {expandedRow === row._id ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>

                      {expandedRow === row._id && getNarration(row) && (
                        <tr className="tele-export-journal-expanded-row">
                          <td colSpan="8" style={{ padding: 0 }}>
                            <div className="tele-export-journal-expanded-content">
                              <div className="tele-export-journal-expanded-grid">
                                <div className="tele-export-journal-exp-item tele-export-journal-exp-narration">
                                  <span className="tele-export-journal-exp-label">Narration</span>
                                  <span className="tele-export-journal-exp-value">{getNarration(row)}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalSalary;
